import clsx from 'clsx';
import Style from './ModalFrame.module.scss';
import { FC, PropsWithChildren, ReactNode } from 'react';
import { DialogModal } from '../../../components/dialogModal/dialogModal.component';
import { FooterButtonProps, ModalSize } from '../modal-manager.types';
import { useTranslation } from 'react-i18next';
import { AppInputError, AppInputErrorProps } from 'uikit/wrappers';
import { AppButtonWithTooltip } from 'uikit/buttons/buttonWithTooltip/buttonWithTooltip';
import { AppButtonProps } from 'uikit/buttons/button/button.component';

type FooterButtonWithOnClick = FooterButtonProps &
  Pick<AppButtonProps, 'onClick' | 'isLoading' | 'hasLoaded' | 'onAnimationComplete'>;

type DefaultFooterProps = {
  generalError?: AppInputErrorProps['errors'];
  cancelButton?: FooterButtonWithOnClick | undefined;
  confirmButton: FooterButtonWithOnClick | undefined;
  className?: string;
};

type ModalFrameProps = PropsWithChildren<{
  title?: string;
  size: ModalSize;
  stackIndex: number;
  isTopmost: boolean;
  defaultFooter?: DefaultFooterProps;
  renderFooter?: ReactNode;
  disabled?: boolean;
  onClose: () => void;
}>;

export const ModalFrame = ({
  children,
  title,
  size,
  stackIndex,
  isTopmost,
  defaultFooter,
  renderFooter,
  disabled,
  onClose,
}: ModalFrameProps) => {
  const sizeClassName = {
    [ModalSize.Small]: Style.small,
    [ModalSize.Medium]: Style.medium,
    [ModalSize.Large]: Style.large,
  }[size];

  const footer = renderFooter ?? (defaultFooter && <DefaultFooter {...defaultFooter} size={size} />);

  return (
    <DialogModal
      visible={true}
      title={title}
      onHide={onClose}
      isDisabled={disabled || !isTopmost}
      isClickOutside={false}
      containerClassName={clsx(sizeClassName, !isTopmost && Style.inactive)}
      testId={`global-modal-${stackIndex}`}
      renderFooter={footer}
    >
      {children}
    </DialogModal>
  );
};

export const DefaultFooter: FC<DefaultFooterProps & { size: ModalSize }> = ({
  generalError,
  cancelButton,
  confirmButton,
  className,
  size,
}) => {
  const { t } = useTranslation();

  return (
    <div className={clsx(Style.footer, className)}>
      <AppInputError hideBorder errors={generalError} className={Style.error} />
      <div
        className={clsx(Style.footerButtons, {
          [Style.fullWidth]: size === ModalSize.Small,
          [Style.alignRight]: size !== ModalSize.Small,
        })}
      >
        {cancelButton && (
          <div className={Style.firstColumn}>
            <AppButtonWithTooltip
              variant={'button'}
              testId={cancelButton.testId}
              isLoading={cancelButton.isLoading}
              hasLoaded={cancelButton.hasLoaded}
              onAnimationComplete={cancelButton.onAnimationComplete}
              loadingText={cancelButton.loadingText ?? t('Common_loading')}
              disabled={cancelButton.disabled}
              onClick={cancelButton.onClick}
              text={cancelButton.text ?? t('Common_Cancel')}
              colorSchema={cancelButton.colorSchema ?? 'secondary'}
              disabledTooltip={!cancelButton.tooltip}
              tooltip={cancelButton.tooltip ?? ''}
              tooltipClassName={Style.buttonTooltipHost}
              className={Style.cancelButton}
            />
          </div>
        )}
        {confirmButton && (
          <div className={Style.secondColumn}>
            <AppButtonWithTooltip
              variant={'button'}
              testId={confirmButton.testId}
              isLoading={confirmButton.isLoading}
              hasLoaded={confirmButton.hasLoaded}
              onAnimationComplete={confirmButton.onAnimationComplete}
              loadingText={confirmButton.loadingText ?? t('Common_loading')}
              disabled={confirmButton.disabled}
              onClick={confirmButton.onClick}
              text={confirmButton.text ?? t('Common_Save')}
              colorSchema={confirmButton.colorSchema ?? 'primary'}
              disabledTooltip={!confirmButton.tooltip}
              tooltip={confirmButton.tooltip ?? ''}
              tooltipClassName={Style.buttonTooltipHost}
              className={Style.confirmButton}
            />
          </div>
        )}
      </div>
    </div>
  );
};
